import React, { useState, useCallback } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../../components/ui/tabs';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from '../../components/ui/alert-dialog';
import {
  Gift, Sparkles, RotateCcw, Phone, Coins, Hourglass, ShieldCheck,
  Search, Loader2, AlertTriangle, CheckCircle2, User, ArrowDownCircle,
} from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────────────────────
// Admin → Points → Issue  (Phase 5 Loyalty Engine)
//
// Three tabs that share a single phone-lookup + balance preview:
//   • Loyalty Credit  — non-expiring, reason required
//   • Promo Credit    — expiring (1h..720h), campaign_name required
//   • Reversal        — negative deduction, reason required
//
// Each tab opens a Confirm dialog before hitting the backend so admins
// never fat-finger a credit. Success screen shows the txn id + new balance.
// ─────────────────────────────────────────────────────────────────────────────

const PHONE_REGEX = /^[6-9]\d{9}$/;

const EXPIRY_PRESETS = [
  { value: 12,  label: '12 hours' },
  { value: 24,  label: '1 day' },
  { value: 72,  label: '3 days' },
  { value: 168, label: '7 days' },
  { value: 360, label: '15 days' },
  { value: 720, label: '30 days (max)' },
];

function formatNumber(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-IN');
}


export default function PointsIssuePage() {
  // Shared phone + customer state
  const [phone, setPhone]         = useState('');
  const [lookup, setLookup]       = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError]     = useState(null);

  // Tab-local forms
  const [loyaltyAmount, setLoyaltyAmount]   = useState('');
  const [loyaltyReason, setLoyaltyReason]   = useState('');

  const [promoAmount, setPromoAmount]       = useState('');
  const [promoExpiry, setPromoExpiry]       = useState(24);
  const [promoCampaign, setPromoCampaign]   = useState('');

  const [reverseAmount, setReverseAmount]   = useState('');
  const [reverseReason, setReverseReason]   = useState('');

  // Confirm dialog
  const [pendingAction, setPendingAction]   = useState(null); // {type, payload}
  const [submitting, setSubmitting]         = useState(false);
  const [lastSuccess, setLastSuccess]       = useState(null);

  const customer = lookup?.customer;
  const balance  = lookup?.balance;
  const phoneValid = PHONE_REGEX.test(phone.trim());

  // ── Phone lookup ─────────────────────────────────────────────────────────
  const runLookup = useCallback(async (e) => {
    e?.preventDefault();
    if (!phoneValid) {
      toast.error('Enter a valid 10-digit Indian mobile number');
      return;
    }
    setLookupLoading(true);
    setLookupError(null);
    setLastSuccess(null);
    try {
      const res = await adminApi.lookupCustomerForIssue(phone.trim());
      setLookup(res.data);
    } catch (err) {
      const d   = err?.response?.data?.detail;
      const msg = (typeof d === 'object' && d?.message) ? d.message : (d || 'Customer not found');
      setLookupError(msg);
      setLookup(null);
      toast.error(msg);
    } finally {
      setLookupLoading(false);
    }
  }, [phone, phoneValid]);

  const refreshBalance = useCallback(async () => {
    if (!customer?.phone) return;
    try {
      const res = await adminApi.lookupCustomerForIssue(customer.phone);
      setLookup(res.data);
    } catch { /* ignore */ }
  }, [customer]);

  // ── Confirm helpers ──────────────────────────────────────────────────────
  const askConfirmLoyalty = () => {
    const amt = parseInt(loyaltyAmount, 10);
    if (!customer) { toast.error('Look up a customer first'); return; }
    if (!Number.isInteger(amt) || amt <= 0) { toast.error('Enter a positive amount'); return; }
    if (!loyaltyReason.trim()) { toast.error('Reason is required'); return; }
    setPendingAction({
      type:  'loyalty',
      title: 'Issue Loyalty Credit',
      summary: `+${amt} non-expiring pts to ${customer.full_name}`,
      payload: { amount: amt, reason: loyaltyReason.trim() },
    });
  };

  const askConfirmPromo = () => {
    const amt = parseInt(promoAmount, 10);
    if (!customer) { toast.error('Look up a customer first'); return; }
    if (!Number.isInteger(amt) || amt <= 0) { toast.error('Enter a positive amount'); return; }
    if (!promoCampaign.trim()) { toast.error('Campaign name is required'); return; }
    const exp = parseInt(promoExpiry, 10);
    if (!Number.isInteger(exp) || exp <= 0 || exp > 720) {
      toast.error('Expiry must be between 1 and 720 hours');
      return;
    }
    setPendingAction({
      type:  'promo',
      title: 'Issue Promo Credit',
      summary: `+${amt} pts to ${customer.full_name} · expires in ${exp}h`,
      payload: { amount: amt, expiry_hours: exp, campaign_name: promoCampaign.trim() },
    });
  };

  const askConfirmReverse = () => {
    const amt = parseInt(reverseAmount, 10);
    if (!customer) { toast.error('Look up a customer first'); return; }
    if (!Number.isInteger(amt) || amt <= 0) { toast.error('Enter a positive amount'); return; }
    if (!reverseReason.trim()) { toast.error('Reason is required'); return; }
    if (amt > (balance?.available || 0)) {
      toast.error(`Customer only has ${balance?.available || 0} pts — cannot reverse ${amt}`);
      return;
    }
    setPendingAction({
      type:  'reverse',
      title: 'Reverse Points',
      summary: `-${amt} pts from ${customer.full_name}`,
      payload: { amount: amt, reason: reverseReason.trim() },
    });
  };

  // ── Commit ──────────────────────────────────────────────────────────────
  const commitAction = async () => {
    if (!pendingAction || !customer) return;
    setSubmitting(true);
    try {
      const { type, payload } = pendingAction;
      let res;
      if (type === 'loyalty') {
        res = await adminApi.issueLoyalty(customer.phone, payload.amount, payload.reason);
        setLoyaltyAmount(''); setLoyaltyReason('');
      } else if (type === 'promo') {
        res = await adminApi.issuePromo(
          customer.phone, payload.amount, payload.expiry_hours, payload.campaign_name,
        );
        setPromoAmount(''); setPromoCampaign('');
      } else if (type === 'reverse') {
        res = await adminApi.reverseCredit(customer.phone, payload.amount, payload.reason);
        setReverseAmount(''); setReverseReason('');
      }
      setLastSuccess({ type, ...res.data });
      setLookup((prev) => prev ? { ...prev, balance: res.data.balance } : prev);
      toast.success('Done.');
    } catch (err) {
      const d   = err?.response?.data?.detail;
      const msg = (typeof d === 'object' && d?.message) ? d.message : (d || 'Operation failed');
      toast.error(msg);
    } finally {
      setSubmitting(false);
      setPendingAction(null);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <AdminShell title="Points · Issue">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-points-issue-page">
        {/* Header */}
        <header className="space-y-1">
          <h1 className="text-2xl font-bold text-[#F2F2F2] flex items-center gap-2">
            <Gift size={20} className="text-[#D4A843]" /> Issue Points
          </h1>
          <p className="text-sm text-[#B9B9B9]">
            Manually grant loyalty / promo credits or reverse a prior credit. All issuances are
            append-only ledger rows tagged with your admin ID.
          </p>
        </header>

        {/* ── Phone lookup card ─────────────────────────────────────────── */}
        <Card className="p-5 rounded-2xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] space-y-3">
          <form onSubmit={runLookup} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
            <div className="flex-1 space-y-1">
              <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
                <Phone size={11} /> Customer phone
              </Label>
              <Input
                data-testid="issue-phone-input"
                inputMode="numeric"
                placeholder="9876543210"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="bg-[#111] border-[rgba(255,255,255,0.1)] text-[#F2F2F2] h-11"
                autoComplete="off"
              />
            </div>
            <Button
              type="submit"
              data-testid="issue-lookup-btn"
              disabled={!phoneValid || lookupLoading}
              className="h-11 btn-gold rounded-xl px-5"
            >
              {lookupLoading
                ? <><Loader2 size={14} className="animate-spin mr-2" /> Looking up…</>
                : <><Search size={14} className="mr-2" /> Look up</>}
            </Button>
          </form>

          {lookupError && !customer && (
            <div className="text-[12px] text-[#FF8A8A] flex items-start gap-2">
              <AlertTriangle size={12} className="mt-0.5" /> {lookupError}
            </div>
          )}

          {customer && (
            <div className="pt-2 border-t border-[rgba(255,255,255,0.06)] grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-[rgba(212,168,67,0.12)]">
                  <User size={16} className="text-[#D4A843]" />
                </div>
                <div className="min-w-0">
                  <p
                    className="text-sm font-semibold text-[#F2F2F2] truncate"
                    data-testid="issue-customer-name"
                  >
                    {customer.full_name}
                  </p>
                  <p className="text-xs text-[#B9B9B9] flex items-center gap-1">
                    <Phone size={9} /> {customer.phone || customer.phone_e164}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-right">
                <BalanceStat label="Available" value={balance?.available} color="#D4A843" testId="issue-bal-available" />
                <BalanceStat label="Expiring"  value={balance?.expiring}  color="#FF8A8A" />
                <BalanceStat label="Loyalty"   value={balance?.non_expiring} color="#60A5FA" />
              </div>
            </div>
          )}
        </Card>

        {/* ── Last success banner ─────────────────────────────────────── */}
        {lastSuccess && customer && (
          <Card
            className="p-4 rounded-2xl border"
            style={{ background: 'rgba(74,222,128,0.06)', borderColor: 'rgba(74,222,128,0.35)' }}
            data-testid="issue-success-banner"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 size={18} className="text-[#4ADE80] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-[#F2F2F2] font-medium">
                  {lastSuccess.type === 'loyalty' && 'Loyalty credit issued'}
                  {lastSuccess.type === 'promo'   && 'Promo credit issued'}
                  {lastSuccess.type === 'reverse' && 'Reversal recorded'}
                </p>
                <p className="text-xs text-[#B9B9B9] mt-0.5">
                  {Math.abs(lastSuccess.transaction?.points)} pts {lastSuccess.type === 'reverse' ? 'deducted from' : 'credited to'}{' '}
                  <strong className="text-[#F2F2F2]">{customer.full_name}</strong>
                  {' · '}
                  Txn{' '}
                  <code className="text-[#6B6B6B]">{lastSuccess.transaction?.id?.slice(0, 8)}…</code>
                  {' · '}
                  New balance{' '}
                  <span className="font-mono text-[#D4A843] font-semibold">
                    {formatNumber(lastSuccess.balance?.available)}
                  </span>{' '}pts
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* ── Tabs ─────────────────────────────────────────────────────── */}
        <Tabs defaultValue="loyalty" className="space-y-4">
          <TabsList
            data-testid="issue-tabs"
            className="grid grid-cols-3 bg-[rgba(255,255,255,0.04)] p-1 rounded-xl h-auto"
          >
            <TabsTrigger value="loyalty" data-testid="tab-loyalty" className="rounded-lg data-[state=active]:bg-[rgba(212,168,67,0.2)] data-[state=active]:text-[#D4A843]">
              <Sparkles size={13} className="mr-1.5" /> Loyalty
            </TabsTrigger>
            <TabsTrigger value="promo" data-testid="tab-promo" className="rounded-lg data-[state=active]:bg-[rgba(212,168,67,0.2)] data-[state=active]:text-[#D4A843]">
              <Gift size={13} className="mr-1.5" /> Promo
            </TabsTrigger>
            <TabsTrigger value="reverse" data-testid="tab-reverse" className="rounded-lg data-[state=active]:bg-[rgba(255,77,77,0.18)] data-[state=active]:text-[#FF8A8A]">
              <RotateCcw size={13} className="mr-1.5" /> Reverse
            </TabsTrigger>
          </TabsList>

          {/* ── LOYALTY TAB ── */}
          <TabsContent value="loyalty">
            <TabCard
              icon={<Sparkles size={16} className="text-[#60A5FA]" />}
              title="Loyalty credit"
              hint="Non-expiring goodwill points. Lives forever in the wallet until redeemed."
              testId="loyalty-form"
            >
              <FormGrid>
                <AmountField
                  testId="loyalty-amount-input"
                  value={loyaltyAmount}
                  onChange={setLoyaltyAmount}
                />
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-[#B9B9B9] text-xs">Reason</Label>
                  <Textarea
                    data-testid="loyalty-reason-input"
                    rows={2}
                    placeholder="e.g. Birthday VIP gesture · Service recovery"
                    value={loyaltyReason}
                    onChange={(e) => setLoyaltyReason(e.target.value)}
                    className="bg-[#111] border-[rgba(255,255,255,0.1)] text-[#F2F2F2] resize-none"
                  />
                </div>
              </FormGrid>
              <SubmitRow
                disabled={!customer}
                onClick={askConfirmLoyalty}
                label="Issue Loyalty Credit"
                tone="gold"
                testId="loyalty-submit-btn"
              />
            </TabCard>
          </TabsContent>

          {/* ── PROMO TAB ── */}
          <TabsContent value="promo">
            <TabCard
              icon={<Gift size={16} className="text-[#D4A843]" />}
              title="Promotional credit"
              hint="Expiring bonus for a specific campaign. Max 30 days."
              testId="promo-form"
            >
              <FormGrid>
                <AmountField
                  testId="promo-amount-input"
                  value={promoAmount}
                  onChange={setPromoAmount}
                />
                <div className="space-y-2">
                  <Label className="text-[#B9B9B9] text-xs">Expiry</Label>
                  <select
                    data-testid="promo-expiry-select"
                    value={promoExpiry}
                    onChange={(e) => setPromoExpiry(parseInt(e.target.value, 10))}
                    className="bg-[#111] border border-[rgba(255,255,255,0.1)] text-[#F2F2F2] rounded-md h-10 px-3 w-full text-sm"
                  >
                    {EXPIRY_PRESETS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-[#B9B9B9] text-xs">Campaign name</Label>
                  <Input
                    data-testid="promo-campaign-input"
                    placeholder="e.g. Diwali Bonus 2026 · Friday Happy Hour"
                    value={promoCampaign}
                    onChange={(e) => setPromoCampaign(e.target.value)}
                    className="bg-[#111] border-[rgba(255,255,255,0.1)] text-[#F2F2F2]"
                  />
                </div>
              </FormGrid>
              <SubmitRow
                disabled={!customer}
                onClick={askConfirmPromo}
                label="Issue Promo Credit"
                tone="gold"
                testId="promo-submit-btn"
              />
            </TabCard>
          </TabsContent>

          {/* ── REVERSAL TAB ── */}
          <TabsContent value="reverse">
            <TabCard
              icon={<RotateCcw size={16} className="text-[#FF8A8A]" />}
              title="Reverse points"
              hint="Append-only deduction from the customer's active balance. Original credit rows are never modified."
              testId="reverse-form"
              tone="red"
            >
              <FormGrid>
                <AmountField
                  testId="reverse-amount-input"
                  value={reverseAmount}
                  onChange={setReverseAmount}
                  max={balance?.available}
                />
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-[#B9B9B9] text-xs">Reason</Label>
                  <Textarea
                    data-testid="reverse-reason-input"
                    rows={2}
                    placeholder="e.g. Cover-charge miscalculation · Refund requested"
                    value={reverseReason}
                    onChange={(e) => setReverseReason(e.target.value)}
                    className="bg-[#111] border-[rgba(255,255,255,0.1)] text-[#F2F2F2] resize-none"
                  />
                </div>
              </FormGrid>
              <div className="p-3 rounded-xl bg-[rgba(255,77,77,0.06)] border border-[rgba(255,77,77,0.25)] flex items-start gap-2 text-[12px] text-[#FF8A8A]">
                <ShieldCheck size={12} className="mt-0.5 flex-shrink-0" />
                Reversal cannot exceed{' '}
                <strong className="font-mono">{formatNumber(balance?.available)}</strong>{' '}
                pts (current available balance).
              </div>
              <SubmitRow
                disabled={!customer}
                onClick={askConfirmReverse}
                label="Record Reversal"
                tone="red"
                testId="reverse-submit-btn"
              />
            </TabCard>
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Confirm dialog ──────────────────────────────────────────── */}
      <AlertDialog open={!!pendingAction} onOpenChange={(o) => !o && setPendingAction(null)}>
        <AlertDialogContent
          className="bg-[#0F0F0F] border-[rgba(255,255,255,0.1)] text-[#F2F2F2] rounded-2xl"
          data-testid="issue-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F2F2F2] flex items-center gap-2">
              {pendingAction?.type === 'reverse'
                ? <RotateCcw size={16} className="text-[#FF8A8A]" />
                : <Coins size={16} className="text-[#D4A843]" />}
              {pendingAction?.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#B9B9B9] pt-1">
              {pendingAction?.summary}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-[rgba(255,255,255,0.04)] border-[rgba(255,255,255,0.1)] text-[#B9B9B9] hover:bg-[rgba(255,255,255,0.08)]"
              data-testid="issue-confirm-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={commitAction}
              disabled={submitting}
              className={
                pendingAction?.type === 'reverse'
                  ? 'bg-[#FF4D4D] hover:bg-[#FF6B6B] text-white'
                  : 'btn-gold'
              }
              data-testid="issue-confirm-commit"
            >
              {submitting
                ? <><Loader2 size={14} className="animate-spin mr-2" /> Committing…</>
                : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}


// ─── Reusable small components ────────────────────────────────────────────
function BalanceStat({ label, value, color, testId }) {
  return (
    <div className="text-right" data-testid={testId}>
      <p className="text-[9px] text-[#6B6B6B] uppercase tracking-widest">{label}</p>
      <p className="text-base font-mono font-semibold" style={{ color }}>
        {formatNumber(value ?? 0)}
      </p>
    </div>
  );
}

function FormGrid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function AmountField({ testId, value, onChange, max }) {
  return (
    <div className="space-y-2">
      <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5">
        <Coins size={11} /> Points
        {max != null && <span className="ml-1 text-[10px] text-[#6B6B6B]">(max {formatNumber(max)})</span>}
      </Label>
      <Input
        data-testid={testId}
        type="number"
        min={1}
        max={max || undefined}
        placeholder="e.g. 250"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[#111] border-[rgba(255,255,255,0.1)] text-[#F2F2F2] text-lg font-mono h-11"
      />
    </div>
  );
}

function SubmitRow({ disabled, onClick, label, tone, testId }) {
  const classes =
    tone === 'red'
      ? 'bg-[#FF4D4D] hover:bg-[#FF6B6B] text-white disabled:opacity-40'
      : 'btn-gold disabled:opacity-40';
  return (
    <div className="flex justify-end pt-2">
      <Button
        data-testid={testId}
        onClick={onClick}
        disabled={disabled}
        className={`${classes} h-11 rounded-xl px-6 font-semibold`}
      >
        <ArrowDownCircle size={14} className="mr-2" /> {label}
      </Button>
    </div>
  );
}

function TabCard({ icon, title, hint, children, testId, tone }) {
  const border = tone === 'red'
    ? 'border-[rgba(255,77,77,0.25)]'
    : 'border-[rgba(255,255,255,0.07)]';
  return (
    <Card
      data-testid={testId}
      className={`p-5 rounded-2xl bg-[rgba(255,255,255,0.02)] ${border} space-y-4`}
    >
      <header className="flex items-start gap-2">
        <div className="mt-0.5">{icon}</div>
        <div>
          <h2 className="text-sm font-semibold text-[#F2F2F2]">{title}</h2>
          <p className="text-[11px] text-[#B9B9B9] mt-0.5 max-w-md">{hint}</p>
        </div>
      </header>
      {children}
    </Card>
  );
}
